import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import type { MasterProductFamily } from './types';
import { masterProductFamiliesEndpoint } from './use-get-master-product-families-api';

/**
 * Families whose name resembles the given one.
 *
 * A narrow lookup for duplicate guards: the caller supplies a candidate name
 * and decides for itself what counts as too close. The endpoint's
 * `universalName` filter is a contains-match, so this returns a shortlist to
 * score locally rather than the whole catalogue.
 *
 * Deliberately separate from useGetMasterProductFamiliesApi, which reads its
 * filters from the page's URL params and so cannot be pointed at an arbitrary
 * name.
 */
const endpoint = masterProductFamiliesEndpoint;
export const findSimilarFamiliesEndpointIdentifier = `${endpoint}/similar`;

interface ResponseData {
  data: MasterProductFamily[];
}

type TData = ResponseData;
type TError = AxiosError;
type TQueryKey = [string, string];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useFindSimilarFamiliesApi(name: string | undefined, options?: Options): ReturnType {
  const isLoggedIn = useIsLoggedIn();
  const trimmed = (name ?? '').trim();

  const queryFn = async () => {
    const { data } = await http.get(endpoint, { params: { page: 1, limit: 20, universalName: trimmed } });
    return data;
  };

  return useQueryWrapper([findSimilarFamiliesEndpointIdentifier, trimmed], queryFn, {
    enabled: isLoggedIn && trimmed.length >= 2,
    // A duplicate guard does not need to be fresh to the second.
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}
